// src/pages/DuitlehIntro.tsx
import React from "react";
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    Chip,
    Avatar,
    Stack,
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Speed as SpeedIcon,
    Security as SecurityIcon,
    AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";

const FeatureCard: React.FC<{ title: string; desc: string; icon: React.ReactNode }> = ({
    title,
    desc,
    icon,
}) => (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar sx={{ bgcolor: "primary.main" }}>{icon}</Avatar>
            <Box>
                <Typography variant="h6" component="h3" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {desc}
                </Typography>
            </Box>
        </Stack>
    </Paper>
);

export default function DuitlehIntro() {
    return (
        <Box component="main" sx={{ bgcolor: "#bed5f5", minHeight: "100vh", pb: 8 }}>
            {/* HERO */}
            <Box
                sx={{
                    bgcolor: "linear-gradient(135deg, #1976d2 0%, #60a5fa 100%)",
                    color: "white",
                    py: 10,
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}
                >
                    {/* Left */}
                    <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 50%" } }}>
                        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 2, color: "grey.900" }}>
                            Duitleh — Simple finance, smarter decisions
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 3, color: "grey.900" }}>
                            Track your wallets, categorize spending, and get instant insights...
                        </Typography>

                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{ borderRadius: 2, px: 2, textTransform: 'none' }}
                                href="/dashboard"
                            >
                                Get Started
                            </Button>
                        </Stack>

                        <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip label="Open Source" size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }} />
                            <Chip label="No tracking" size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }} />
                            <Chip label="Lightweight" size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }} />
                        </Box>
                    </Box>

                    {/* Right */}
                    {/*  */}
                </Container>
            </Box>

            {/* FEATURES */}
            <Container maxWidth="lg" sx={{ mt: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                    What Duitleh does
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Built for simplicity — Duitleh helps you keep track of money flow with minimal fuss. A
                    few highlights:
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                        gap: 3,
                    }}
                >
                    <FeatureCard
                        title="Fast & Reliable"
                        desc="Lightweight React + Node stack so the app feels snappy even on low-memory devices."
                        icon={<SpeedIcon />}
                    />
                    <FeatureCard
                        title="Secure"
                        desc="JWT auth, encrypted credentials, and minimal data collection — your privacy first."
                        icon={<SecurityIcon />}
                    />
                    <FeatureCard
                        title="Wallets & Categories"
                        desc="Manage multiple wallets and categorize transactions for clearer spending insights."
                        icon={<WalletIcon />}
                    />
                    <FeatureCard
                        title="Reports & Insights"
                        desc="Simple analytics to show where your money goes — daily, weekly, monthly views."
                        icon={<CheckCircleIcon />}
                    />
                    <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                        <Typography variant="h6" gutterBottom>
                            Easy Integrations
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Email for notifications, optional third-party login (Apple/Github), and easy DB
                            backups.
                        </Typography>
                    </Paper>
                    <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                        <Typography variant="h6" gutterBottom>
                            Developer friendly
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Clean codebase, Docker & CI/CD friendly. Deploy to VPS or platform of your choice.
                        </Typography>
                    </Paper>
                </Box>
            </Container>

            {/* HOW IT WORKS */}
            <Container maxWidth="lg" sx={{ mt: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                    How it works
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                        gap: 3,
                        mt: 1,
                    }}
                >
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6">1. Add Wallets</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create wallets (cash, bank, e-wallets) and set opening balances.
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6">2. Add Transactions</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Quickly add income/expense, assign category and wallet.
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6">3. See Reports</Typography>
                        <Typography variant="body2" color="text.secondary">
                            View summary and filter by date, wallet, or category.
                        </Typography>
                    </Paper>
                </Box>
            </Container>

            {/* CTA */}
            <Container maxWidth="md" sx={{ mt: 6, textAlign: "center" }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        Ready to take control of your money?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Try Duitleh on your device — lightweight and built for everyday tracking.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
                        <Button variant="contained" size="large" href="/register" sx={{ borderRadius: 2 }}>
                            Create Account
                        </Button>
                        <Button variant="outlined" size="large" href="/login" sx={{ borderRadius: 2 }}>
                            Sign In
                        </Button>
                    </Stack>
                </Paper>
            </Container>

            {/* FOOTER */}
            <Box component="footer" sx={{ mt: 8, py: 4, bgcolor: "background.paper" }}>
                <Container
                    maxWidth="lg"
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}
                >
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Duitleh
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            © {new Date().getFullYear()} Duitleh
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Built with ❤️ •{" "}
                            <a href="/docs" style={{ color: "inherit" }}>Docs</a> •{" "}
                            <a href="/privacy" style={{ color: "inherit" }}>Privacy</a>
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
